import React from "react";

/**
 * Why a material line left the shelf. Mirrors
 * `backend/services/usage_purpose.py` — keep the three values in step.
 *
 * Staff see and set this (they are the ones holding the roll); they
 * never see what it cost. A note is REQUIRED for anything that is not a
 * plain customer job, because both of the others move money.
 */
export const USAGE_PURPOSES = [
  {
    value: "customer_job",
    label: "Customer job",
    short: "Customer",
    help: "Normal material used on the customer's vehicle.",
    noteRequired: false,
    badge: "bg-slate-700/60 text-slate-300 border-slate-600",
  },
  {
    value: "goodwill",
    label: "Goodwill (given to customer)",
    short: "Goodwill",
    help: "Extra or better material given on purpose. Stays in the job's cost.",
    noteRequired: true,
    badge: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  },
  {
    value: "internal_use",
    label: "Internal use (not a customer cost)",
    short: "Internal use",
    help: "Own vehicle, workshop or demo. Kept out of job cost and margin.",
    noteRequired: true,
    badge: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  },
];

export const purposeMeta = (value) =>
  USAGE_PURPOSES.find((p) => p.value === value) || USAGE_PURPOSES[0];

export const noteRequiredFor = (value) => purposeMeta(value).noteRequired;

/** Badge shown on a saved material row. Nothing for a plain customer job —
 *  a badge on every single row would be noise. */
export const UsagePurposeBadge = ({ purpose, note, testId }) => {
  const meta = purposeMeta(purpose);
  if (meta.value === "customer_job") return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${meta.badge}`}
      title={note ? `${meta.label} — ${note}` : meta.label}
      data-testid={testId}
    >
      {meta.short}
    </span>
  );
};

/**
 * The picker staff use when LOGGING a line. Controlled; the parent owns
 * both values so it can block its own Save until a required note exists.
 */
export const UsagePurposePicker = ({
  purpose = "customer_job",
  note = "",
  onChange,
  idSuffix = "",
  compact = false,
}) => {
  const meta = purposeMeta(purpose);
  const needsNote = meta.noteRequired;
  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      <select
        value={purpose}
        onChange={(e) => onChange(e.target.value, note)}
        className="w-full bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1.5"
        data-testid={`usage-purpose-select${idSuffix}`}
      >
        {USAGE_PURPOSES.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
      {needsNote && (
        <input
          type="text"
          value={note}
          onChange={(e) => onChange(purpose, e.target.value)}
          placeholder="Required — what was it for?"
          maxLength={500}
          className={`w-full bg-slate-900 border text-white text-xs rounded px-2 py-1.5 ${
            note.trim() ? "border-slate-700" : "border-amber-500/60"
          }`}
          data-testid={`usage-note-input${idSuffix}`}
        />
      )}
      {!compact && (
        <p className="text-[11px] text-slate-500">{meta.help}</p>
      )}
    </div>
  );
};
